/**
 * Product Selector Blocks — Shared JS
 *
 * Handles interactivity for selling-plan-toggle, bundle-cards,
 * subscription-details, and total-bar blocks inside _product-details.
 *
 * Uses event delegation on the product-details container.
 * Integrates with the existing product-form-component via:
 * - variant:update events (from variant-picker)
 * - FormData injection (selling_plan hidden input)
 */
(function () {
  'use strict';

  /* ---- Helpers ---- */

  /**
   * Format cents to money string using Shopify money_format.
   * @param {number} cents
   * @param {string} [moneyFormat]
   * @returns {string}
   */
  function formatMoney(cents, moneyFormat) {
    if (typeof cents !== 'number') return '';
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimals = Math.floor(cents / 100).toString();
    var amountWithComma = amount.replace('.', ',');

    if (!moneyFormat) return '$' + amount;

    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals);
  }

  /**
   * Strip trailing .00 decimals from a formatted price string based on the
   * `data-decimal-display` attribute on a parent `add-to-cart-component`.
   * @param {string} text - Formatted price string
   * @param {Element} el - Element inside an add-to-cart-component
   * @returns {string}
   */
  function applyDecimalDisplay(text, el) {
    if (!el || !text) return text;
    var wrap = el.closest('add-to-cart-component');
    if (!wrap) return text;
    var mode = wrap.getAttribute('data-decimal-display');
    if (!mode) return text;
    if (mode === 'hide') return text.replace('.00', '');
    if (mode === 'auto' && text.indexOf('.00') !== -1) return text.replace('.00', '');
    return text;
  }

  /**
   * Find the closest product-form-component from an element.
   * @param {Element} el
   * @returns {HTMLElement|null}
   */
  function findProductForm(el) {
    var details = el.closest('.product-details');
    if (!details) return null;
    return details.querySelector('product-form-component');
  }

  /**
   * Get the actual <form> inside a product-form-component.
   * @param {HTMLElement} formComponent
   * @returns {HTMLFormElement|null}
   */
  function getForm(formComponent) {
    return formComponent.querySelector('form[data-type="add-to-cart-form"]');
  }

  /**
   * Get or create a selling_plan hidden input in the form.
   * @param {HTMLFormElement} form
   * @returns {HTMLInputElement}
   */
  function getOrCreateSellingPlanInput(form) {
    var input = form.querySelector('input[name="selling_plan"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.value = '';
      form.appendChild(input);
    }
    return input;
  }

  /**
   * Get product JSON embedded in the page.
   * @param {Element} container
   * @returns {Object|null}
   */
  function getProductJson(container) {
    var script = container.querySelector('script[data-ps-product-json]');
    // In product-information sections, the JSON is at the section level, outside .product-details
    if (!script) {
      var section = container.closest('.shopify-section');
      if (section) {
        script = section.querySelector('script[data-ps-product-json]');
      }
    }
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Get the currently selected variant ID from the form.
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  function getCurrentVariantId(form) {
    var input = form.querySelector('input[name="id"]');
    return input ? input.value : '';
  }

  /**
   * Find a variant by ID in product JSON.
   * @param {Object} product
   * @param {string} variantId
   * @returns {Object|null}
   */
  function findVariant(product, variantId) {
    if (!product || !product.variants) return null;
    var id = parseInt(variantId, 10);
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === id) return product.variants[i];
    }
    return null;
  }

  /* ---- Selling Plan Toggle ---- */

  function initSellingPlanToggle() {
    document.addEventListener('click', function (e) {
      var card = e.target.closest('[data-ps-sp-card]');
      if (!card) return;

      var container = card.closest('[data-ps-selling-plan-toggle]');
      if (!container) return;

      var cards = container.querySelectorAll('[data-ps-sp-card]');
      cards.forEach(function (c) {
        c.setAttribute('aria-checked', 'false');
      });
      card.setAttribute('aria-checked', 'true');

      var sellingPlanId = card.getAttribute('data-ps-selling-plan-id') || '';
      var formComponent = findProductForm(container);
      if (!formComponent) return;

      var form = getForm(formComponent);
      if (!form) return;

      var input = getOrCreateSellingPlanInput(form);
      input.value = sellingPlanId;

      // Show/hide subscription details
      var details = container.closest('.product-details');
      if (details) {
        var subDetails = details.querySelector('[data-ps-subscription-details]');
        if (subDetails) {
          var isSubscription = sellingPlanId !== '';
          subDetails.hidden = !isSubscription;
        }
      }

      // Update prices
      updatePrices(container);
    });
  }

  /* ---- Bundle Cards ---- */

  /**
   * Check if the bundle cards container is in multi-item cart mode.
   * Multi-item mode adds each inner select as a separate cart line item.
   * Single mode (default) adds one bundle variant with selection properties.
   * @param {Element} el - any element inside the bundle cards container
   * @returns {boolean}
   */
  function isMultiItemMode(el) {
    var container = el.closest('[data-ps-bundle-cards]');
    return container ? container.getAttribute('data-ps-cart-mode') === 'multi' : false;
  }

  /**
   * Compute the summed price of all inner loft selects on a bundle card and
   * update the card's price display. Each select holds a variant ID whose price
   * contributes to the bundle total shown on the card.
   * @param {Element} card - [data-ps-bundle-card] element
   * @param {Object} product - product JSON
   * @param {string|null} format - money format string
   */
  function updateInnerSelectPrices(card, product, format) {
    var selects = card.querySelectorAll('[data-ps-group-loft-select]');
    if (!selects || selects.length === 0) return;

    var totalPrice = 0;
    var totalCompare = 0;
    selects.forEach(function (sel) {
      var vid = sel.value;
      var v = findVariant(product, vid);
      if (v) {
        totalPrice += v.price;
        totalCompare += (v.compare_at_price || v.price);
      }
    });

    var priceEl = card.querySelector('[data-ps-bundle-price]');
    var compareEl = card.querySelector('[data-ps-bundle-compare-price]');
    if (priceEl) priceEl.textContent = formatMoney(totalPrice, format);
    if (compareEl) {
      if (totalCompare > totalPrice) {
        compareEl.textContent = formatMoney(totalCompare, format);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  function initBundleCards() {
    // Card click — select a bundle card
    document.addEventListener('click', function (e) {
      // Skip if click originated from inner loft select or upsell checkbox
      if (e.target.closest('[data-ps-group-loft-select]')) return;
      if (e.target.closest('[data-ps-upsell]')) return;

      var card = e.target.closest('[data-ps-bundle-card]');
      if (!card) return;

      var container = card.closest('[data-ps-bundle-cards]');
      if (!container) return;

      var cards = container.querySelectorAll('[data-ps-bundle-card]');
      cards.forEach(function (c) {
        c.setAttribute('aria-checked', 'false');
      });
      card.setAttribute('aria-checked', 'true');

      // Sync aria-checked on group wrappers
      var groups = container.querySelectorAll('[data-ps-bundle-group]');
      groups.forEach(function (g) {
        var groupCard = g.querySelector('[data-ps-bundle-card]');
        g.setAttribute('aria-checked', groupCard ? groupCard.getAttribute('aria-checked') : 'false');
      });

      // Sync upsell checkboxes: check selected group's upsell, uncheck others
      container.querySelectorAll('[data-ps-upsell]').forEach(function (/** @type {Element} */ upsell) {
        var checkbox = /** @type {HTMLInputElement|null} */ (upsell.querySelector('.ps-bundle-card__upsell-checkbox'));
        if (!checkbox) return;
        var upsellCard = upsell.closest('[data-ps-bundle-card]');
        if (upsellCard) {
          checkbox.checked = upsellCard.getAttribute('aria-checked') === 'true';
        }
        // Sync price contributor active state
        if (upsell.hasAttribute('data-ps-price-contributor')) {
          upsell.setAttribute('data-ps-contributor-active', checkbox.checked ? 'true' : 'false');
        }
      });

      var variantId = card.getAttribute('data-ps-variant-id');
      if (!variantId) return;

      // Update the product form's variant ID
      var formComponent = findProductForm(container);
      if (!formComponent) return;

      var form = getForm(formComponent);
      if (!form) return;

      var variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updatePrices(container);
    });

    // Grouped card — inner loft select change
    document.addEventListener('change', function (e) {
      if (!e.target.matches('[data-ps-group-loft-select]')) return;

      var card = e.target.closest('[data-ps-bundle-card]');
      if (!card) return;

      var selectedVariantId = e.target.value;
      var multiItem = isMultiItemMode(card);

      // Single mode (default): variant swap — update the card's variant ID
      // and the form so the correct bundle-priced variant is submitted.
      // Multi-item mode: each select becomes a separate cart line item;
      // card price is the sum of all selects' variant prices.
      if (!multiItem) {
        card.setAttribute('data-ps-variant-id', selectedVariantId);
      }

      // If this is the active card, update form + price display
      if (card.getAttribute('aria-checked') === 'true') {
        var container = card.closest('[data-ps-bundle-cards]');
        if (!container) return;

        // In single mode, sync the form's hidden variant input
        if (!multiItem) {
          var formComponent = findProductForm(container);
          if (formComponent) {
            var form = getForm(formComponent);
            if (form) {
              var variantInput = form.querySelector('input[name="id"]');
              if (variantInput) {
                variantInput.value = selectedVariantId;
                variantInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
        }

        var details = container.closest('.product-details');
        var product = getProductJson(details || container);
        if (product) {
          var moneyFormatEl = details ? details.closest('[data-money-format]') : null;
          var format = moneyFormatEl ? moneyFormatEl.getAttribute('data-money-format') : null;

          if (multiItem) {
            updateInnerSelectPrices(card, product, format);
          } else {
            // Single mode — show that variant's price directly
            var variant = findVariant(product, selectedVariantId);
            if (variant) {
              var priceEl = card.querySelector('[data-ps-bundle-price]');
              var compareEl = card.querySelector('[data-ps-bundle-compare-price]');
              if (priceEl) priceEl.textContent = formatMoney(variant.price, format);
              if (compareEl) {
                if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                  compareEl.textContent = formatMoney(variant.compare_at_price, format);
                  compareEl.hidden = false;
                } else {
                  compareEl.hidden = true;
                }
              }
            }
          }
        }

        updatePrices(container);
      }
    });

    // Sync form variant ID on page load for the server-rendered active card.
    // Without this, the form keeps the product's default variant ID instead of
    // the pre-selected bundle card's variant.
    document.querySelectorAll('[data-ps-bundle-cards]').forEach(function (container) {
      var activeCard = container.querySelector('[data-ps-bundle-card][aria-checked="true"]');
      if (!activeCard) return;

      var variantId = activeCard.getAttribute('data-ps-variant-id');
      if (!variantId) return;

      var formComponent = findProductForm(container);
      if (!formComponent) return;

      var form = getForm(formComponent);
      if (!form) return;

      var variantInput = form.querySelector('input[name="id"]');
      if (variantInput && variantInput.value !== variantId) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // In multi-item mode, correct server-rendered prices for cards with inner
      // selects. Liquid renders a single variant price, but multi-item cards
      // should show the sum of all inner selects' variant prices.
      if (isMultiItemMode(container)) {
        var details = container.closest('.product-details');
        var product = getProductJson(details || container);
        if (product) {
          var moneyFormatEl = details ? details.closest('[data-money-format]') : null;
          var fmt = moneyFormatEl ? moneyFormatEl.getAttribute('data-money-format') : null;
          container.querySelectorAll('[data-ps-bundle-card]').forEach(function (card) {
            var cardInnerSelects = card.querySelectorAll('[data-ps-group-loft-select]');
            if (cardInnerSelects && cardInnerSelects.length > 0) {
              updateInnerSelectPrices(card, product, fmt);
            }
          });
        }
      }
    });
  }

  /* ---- Bundle Card Submit (multi-item + upsell) ---- */

  /**
   * Get the active upsell for the current bundle card.
   * @param {Element} details - .product-details container
   * @returns {{variantId: string, quantity: number}|null}
   */
  function getActiveUpsell(details) {
    var activeCard = details.querySelector('[data-ps-bundle-card][aria-checked="true"]');
    if (!activeCard) return null;

    // The upsell lives inside the active card's parent group wrapper
    var group = activeCard.closest('[data-ps-bundle-group]') || activeCard;
    var upsellEl = group.querySelector('[data-ps-upsell]');
    // Fallback: search inside the card itself
    if (!upsellEl) upsellEl = activeCard.querySelector('[data-ps-upsell]');
    if (!upsellEl) return null;

    var checkbox = upsellEl.querySelector('.ps-bundle-card__upsell-checkbox');
    if (!checkbox || !checkbox.checked) return null;

    var upsellVariantId = upsellEl.getAttribute('data-upsell-variant-id');
    var upsellQty = parseInt(upsellEl.getAttribute('data-upsell-quantity'), 10) || 1;
    if (!upsellVariantId) return null;

    var upsellPrice = parseInt(upsellEl.getAttribute('data-ps-contributor-price'), 10) || 0;
    return { variantId: upsellVariantId, quantity: upsellQty, price: upsellPrice };
  }

  /**
   * Collect line item properties from inner loft selects on the active bundle card.
   * The bundle variant is a single SKU at one price — inner selects are customization
   * preferences stored as line item properties.
   * Outputs individual selections (#1, #2, #3) plus a summary line (e.g. "2× Regular, 1× High").
   * Returns null if no inner selects exist.
   * @param {Element} activeCard
   * @returns {Object|null}
   */
  function collectInnerSelectProperties(activeCard) {
    var selects = activeCard.querySelectorAll('[data-ps-group-loft-select]');
    if (!selects || selects.length === 0) return null;

    var labels = [];
    selects.forEach(function (sel) {
      var selectedOption = sel.options ? sel.options[sel.selectedIndex] : null;
      var label = selectedOption ? selectedOption.textContent.trim() : '';
      if (label) {
        labels.push(label);
      }
    });

    if (labels.length === 0) return null;

    // Build a summary like "2× Regular, 1× High"
    var counts = {};
    for (var i = 0; i < labels.length; i++) {
      counts[labels[i]] = (counts[labels[i]] || 0) + 1;
    }
    var parts = [];
    for (var key in counts) {
      if (Object.prototype.hasOwnProperty.call(counts, key)) {
        parts.push(counts[key] + '× ' + key);
      }
    }

    var properties = {};
    properties['Selection'] = parts.join(', ');

    return properties;
  }

  /**
   * Handle bundle-card form submission: collects inner select properties,
   * upsell items, and posts to Cart API in a single request.
   * @param {Event} e - submit event
   * @param {HTMLFormElement} form
   */
  function handleBundleSubmit(e, form) {
    e.preventDefault();

    var details = form.closest('.product-details');
    if (!details) return;

    // Validate variant selection — flash the picker if nothing is checked
    var section = details.closest('.shopify-section') || details;
    var variantPicker = section.querySelector('variant-picker');
    if (variantPicker && variantPicker.validateSelection && !variantPicker.validateSelection()) return;

    var activeCard = details.querySelector('[data-ps-bundle-card][aria-checked="true"]');
    var innerProps = activeCard ? collectInnerSelectProperties(activeCard) : null;
    var upsell = getActiveUpsell(details);

    var mainVariantId = form.querySelector('input[name="id"]');
    if (!mainVariantId) return;

    var sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    var quantityInput = form.querySelector('input[name="quantity"]');
    var formQty = parseInt(quantityInput ? quantityInput.value : '1', 10) || 1;

    // Multi-item mode: each inner select is a separate variant added as its own
    // cart line item. Single mode (default): one bundle variant with selection properties.
    var multiItem = activeCard ? isMultiItemMode(activeCard) : false;
    var innerSelects = (multiItem && activeCard) ? activeCard.querySelectorAll('[data-ps-group-loft-select]') : null;
    var items = [];

    if (innerSelects && innerSelects.length > 0) {
      // Group inner selects by variant ID so identical selections merge into qty > 1
      var selectCounts = {};
      var selectOrder = [];
      innerSelects.forEach(function (sel) {
        var vid = sel.value;
        if (!selectCounts[vid]) {
          selectCounts[vid] = 0;
          selectOrder.push(vid);
        }
        selectCounts[vid] += formQty;
      });

      // Build the bundle group label from the active card's title
      var cardTitleEl = activeCard.querySelector('.ps-bundle-card__title');
      var bundleLabel = cardTitleEl ? cardTitleEl.textContent.trim() : '';

      for (var s = 0; s < selectOrder.length; s++) {
        var vid = selectOrder[s];
        var lineItem = { id: parseInt(vid, 10), quantity: selectCounts[vid] };
        if (sellingPlanInput && sellingPlanInput.value) {
          lineItem.selling_plan = parseInt(sellingPlanInput.value, 10);
        }
        if (bundleLabel) {
          lineItem.properties = { _bundle: bundleLabel };
        }
        items.push(lineItem);
      }
    } else {
      var mainItem = { id: parseInt(mainVariantId.value, 10), quantity: formQty };
      if (sellingPlanInput && sellingPlanInput.value) {
        mainItem.selling_plan = parseInt(sellingPlanInput.value, 10);
      }
      if (innerProps) {
        mainItem.properties = innerProps;
      }
      // Inject offer.cart_text metafield as a visible line item property
      var cartTextMapEl = (details.closest('.shopify-section') || document).querySelector('[data-cart-text-map]');
      if (cartTextMapEl) {
        try {
          var cartTextMap = JSON.parse(cartTextMapEl.textContent);
          var cartText = cartTextMap[mainVariantId.value] || cartTextMap['_product'] || '';
          if (cartText) {
            if (!mainItem.properties) mainItem.properties = {};
            mainItem.properties['Details'] = cartText;
          }
        } catch (e) { /* ignore */ }
      }
      items.push(mainItem);
    }
    if (upsell) {
      var productFormComponent = form.closest('product-form-component');
      var parentProductId = productFormComponent ? (productFormComponent.dataset.productId || '') : '';
      var upsellItem = {
        id: parseInt(upsell.variantId, 10),
        quantity: upsell.quantity,
        properties: {}
      };
      if (upsell.price > 0) {
        // Priced upsell — add as a normal line item so the customer can remove it
        upsellItem.properties._upsell = 'true';
        upsellItem.properties._upsell_parent = parentProductId;
      } else {
        // Free upsell — mark as gift so cart hides qty/remove controls
        upsellItem.properties._free_gift = 'true';
        upsellItem.properties._gift_parent = parentProductId;
        upsellItem.properties._gift_per_parent = String(upsell.quantity);
      }
      items.push(upsellItem);
    }

    // Collect checked cart add-ons
    var addons = getActiveAddons(details);
    var parentProductId = form.closest('product-form-component')
      ? (form.closest('product-form-component').dataset.productId || '')
      : '';
    for (var a = 0; a < addons.length; a++) {
      var addonItem = {
        id: parseInt(addons[a].variantId, 10),
        quantity: 1,
        properties: { _addon: 'true', _addon_parent: parentProductId }
      };
      if (addons[a].isFree) {
        addonItem.properties._free_gift = 'true';
      }
      items.push(addonItem);
    }

    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    var payload = { items: items };
    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
    }

    var addToCartComponent = form.querySelector('add-to-cart-component');
    if (addToCartComponent && addToCartComponent.animateAddToCart) {
      addToCartComponent.animateAddToCart();
    }

    fetch(window.Theme && window.Theme.routes ? window.Theme.routes.cart_add_url : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) { return res.json(); })
      .then(function (response) {
        if (response.status) {
          console.error('Bundle cart add error:', response.message);
          return;
        }

        // Fetch actual cart to get real item_count — /cart/add.js only returns added items,
        // not the total. We need the true total so the badge is set (not added to) correctly.
        var cartSectionIds = [];
        document.querySelectorAll('cart-items-component').forEach(function(el) {
          if (el.dataset && el.dataset.sectionId) cartSectionIds.push(el.dataset.sectionId);
        });
        var cartUrl = '/cart.js' + (cartSectionIds.length ? '?sections=' + cartSectionIds.join(',') : '');

        return fetch(cartUrl).then(function(r) { return r.json(); }).then(function(cart) {
          var cartCount = cart.item_count || 0;
          try {
            sessionStorage.setItem('cart-count', JSON.stringify({ value: String(cartCount), timestamp: Date.now() }));
          } catch(_) {}
          // Direct DOM update — theme cart icon
          var bubbleCount = document.querySelector('[ref="cartBubbleCount"]');
          if (bubbleCount) {
            bubbleCount.textContent = cartCount < 100 ? String(cartCount) : '';
          }
          var cartBubble = document.querySelector('[ref="cartBubble"]');
          if (cartBubble) {
            cartBubble.classList.toggle('visually-hidden', cartCount === 0);
          }
          // Direct DOM update — prefstest nav cart count
          var prefstestCount = document.querySelector('[data-prefstest-cart-count]');
          if (prefstestCount) {
            prefstestCount.textContent = String(cartCount);
          }
          // Dispatch event
          var productFormComponent = form.closest('product-form-component');
          var evt = new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: productFormComponent ? (productFormComponent.id || '') : '',
              data: {
                source: 'bundle-add',
                itemCount: cartCount,
                productId: productFormComponent && productFormComponent.dataset ? productFormComponent.dataset.productId : undefined,
                sections: cart.sections || response.sections
              }
            }
          });
          document.dispatchEvent(evt);
        });
      })
      .catch(function (err) {
        console.error('Bundle cart add error:', err);
      });
  }

  function initBundleCardSubmit() {
    // The theme's component.js registers a capture-phase submit listener on document
    // BEFORE this deferred script loads. That means our document-level capture listener
    // would always fire AFTER the theme's, making stopImmediatePropagation useless.
    //
    // Fix: remove the on:submit attribute from product-form-component elements that
    // contain bundle cards, so the theme's delegation skips them entirely.
    // We then handle form submission ourselves via a direct listener on the form.
    var bundleContainers = document.querySelectorAll('[data-ps-bundle-cards]');
    bundleContainers.forEach(function (container) {
      var details = container.closest('.product-details');
      if (!details) return;

      var productFormComp = details.querySelector('product-form-component[on\\:submit]');
      if (productFormComp) {
        // Save and remove the theme's on:submit so its delegation won't fire
        productFormComp.setAttribute('data-original-on-submit', productFormComp.getAttribute('on:submit'));
        productFormComp.removeAttribute('on:submit');
      }

      var form = details.querySelector('form[action*="/cart/add"]');
      if (form && !form.hasAttribute('data-bundle-submit-bound')) {
        form.setAttribute('data-bundle-submit-bound', 'true');
        form.addEventListener('submit', function (e) {
          handleBundleSubmit(e, form);
        });
      }
    });
  }

  /* ---- Subscription Details frequency ---- */

  function initSubscriptionDetails() {
    document.addEventListener('change', function (e) {
      if (!e.target.matches('[data-ps-frequency-select]')) return;

      var container = e.target.closest('[data-ps-subscription-details]');
      if (!container) return;

      var sellingPlanId = e.target.value;
      var formComponent = findProductForm(container);
      if (!formComponent) return;

      var form = getForm(formComponent);
      if (!form) return;

      var input = getOrCreateSellingPlanInput(form);
      input.value = sellingPlanId;

      // Also update the selling plan toggle if present
      var details = container.closest('.product-details');
      if (details) {
        var toggleContainer = details.querySelector('[data-ps-selling-plan-toggle]');
        if (toggleContainer) {
          var subscribeCard = toggleContainer.querySelector('[data-ps-sp-card][data-ps-selling-plan-id]');
          if (subscribeCard && subscribeCard.getAttribute('aria-checked') === 'true') {
            subscribeCard.setAttribute('data-ps-selling-plan-id', sellingPlanId);
          }
        }
      }
    });
  }

  /* ---- Running Total ---- */

  /**
   * Collect the running total from all price contributors within a product-details container.
   * Base price comes from the variant (or subscription allocation).
   * Additional prices come from [data-ps-price-contributor][data-ps-contributor-active="true"].
   * @param {Element} details - .product-details container
   * @param {number} basePrice - variant or allocation price in cents
   * @param {number} baseComparePrice - compare-at price in cents (0 if none)
   * @returns {{ total: number, compareTotal: number }}
   */
  function collectRunningTotal(details, basePrice, baseComparePrice) {
    var total = basePrice;
    var compareTotal = baseComparePrice || basePrice;
    var seen = {};

    var contributors = details.querySelectorAll(
      '[data-ps-price-contributor][data-ps-contributor-active="true"]'
    );
    contributors.forEach(function (el) {
      // Deduplicate by variant ID — synced addon blocks may have duplicate active contributors
      var variantId = el.getAttribute('data-addon-variant-id');
      if (variantId) {
        if (seen[variantId]) return;
        seen[variantId] = true;
      }
      var price = parseInt(el.getAttribute('data-ps-contributor-price'), 10) || 0;
      var compare = parseInt(el.getAttribute('data-ps-contributor-compare-price'), 10) || price;
      total += price;
      compareTotal += compare;
    });

    return { total: total, compareTotal: compareTotal };
  }

  /* ---- Price Updates ---- */

  function updatePrices(contextEl) {
    var details = contextEl.closest('.product-details');
    if (!details) return;

    var product = getProductJson(details);
    if (!product) return;

    var formComponent = findProductForm(contextEl);
    if (!formComponent) return;

    var form = getForm(formComponent);
    if (!form) return;

    var variantId = getCurrentVariantId(form);
    var variant = findVariant(product, variantId);
    if (!variant) return;

    var moneyFormat = details.closest('[data-money-format]');
    var format = moneyFormat ? moneyFormat.getAttribute('data-money-format') : null;

    // Load offer data JSON if available (variant-level metafield + allocation data)
    var offerDataEl = details.querySelector('[data-ps-sp-offer-data]');
    var offerDataMap = {};
    if (offerDataEl) {
      try {
        var offerDataArr = JSON.parse(offerDataEl.textContent);
        for (var k = 0; k < offerDataArr.length; k++) {
          offerDataMap[String(offerDataArr[k].variant_id)] = offerDataArr[k];
        }
      } catch (e) { /* ignore parse errors */ }
    }
    var variantOfferData = offerDataMap[variantId] || null;

    // Check if a selling plan is selected
    var sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    var sellingPlanId = sellingPlanInput ? sellingPlanInput.value : '';
    var allocation = null;

    // Find allocation from offer data (product | json does NOT include selling_plan_allocations)
    if (sellingPlanId && variantOfferData && variantOfferData.allocations) {
      for (var i = 0; i < variantOfferData.allocations.length; i++) {
        if (String(variantOfferData.allocations[i].selling_plan_id) === sellingPlanId) {
          allocation = variantOfferData.allocations[i];
          break;
        }
      }
    }

    // Compute base price (variant or subscription allocation).
    // For bundle cards with inner selects, the base price is the sum of all
    // inner selects' variant prices, not the single form variant.
    var basePrice = allocation ? allocation.per_delivery_price : variant.price;
    var baseComparePrice = variant.compare_at_price || 0;

    var activeBundle = details.querySelector('[data-ps-bundle-card][aria-checked="true"]');
    if (activeBundle && isMultiItemMode(activeBundle)) {
      var bundleInnerSelects = activeBundle.querySelectorAll('[data-ps-group-loft-select]');
      if (bundleInnerSelects && bundleInnerSelects.length > 0) {
        var sumPrice = 0;
        var sumCompare = 0;
        bundleInnerSelects.forEach(function (sel) {
          var sv = findVariant(product, sel.value);
          if (sv) {
            sumPrice += sv.price;
            sumCompare += (sv.compare_at_price || sv.price);
          }
        });
        basePrice = sumPrice;
        baseComparePrice = sumCompare;
      }
    }

    // Collect running total from all price contributors (upsells, add-ons, etc.)
    var runningTotal = collectRunningTotal(details, basePrice, baseComparePrice);
    var totalSavings = runningTotal.compareTotal > runningTotal.total
      ? runningTotal.compareTotal - runningTotal.total : 0;

    // Update total bar if present
    var totalBar = details.querySelector('[data-ps-total-bar]');
    if (totalBar) {
      var currentPriceEl = totalBar.querySelector('[data-ps-current-price]');
      var comparePriceEl = totalBar.querySelector('[data-ps-compare-price]');
      var saveEl = totalBar.querySelector('[data-ps-save-amount]');

      if (currentPriceEl) currentPriceEl.textContent = formatMoney(runningTotal.total, format);
      if (comparePriceEl) {
        comparePriceEl.textContent = totalSavings > 0 ? formatMoney(runningTotal.compareTotal, format) : '';
        comparePriceEl.hidden = totalSavings <= 0;
      }
      if (saveEl) {
        saveEl.textContent = totalSavings > 0 ? 'Save ' + formatMoney(totalSavings, format) : '';
        saveEl.hidden = totalSavings <= 0;
      }
    }

    // Update add-to-cart button price if present
    var atcPriceEl = details.querySelector('[data-ps-atc-price]');
    if (atcPriceEl) {
      atcPriceEl.textContent = applyDecimalDisplay(formatMoney(runningTotal.total, format), atcPriceEl);

      var atcCompareEl = details.querySelector('[data-ps-atc-compare-price]');
      if (atcCompareEl) {
        if (totalSavings > 0) {
          atcCompareEl.textContent = applyDecimalDisplay(formatMoney(runningTotal.compareTotal, format), atcCompareEl);
          atcCompareEl.hidden = false;
        } else {
          atcCompareEl.hidden = true;
        }
      }
    }

    // Update selling plan card prices + dynamic offer content
    // Note: variant.selling_plan_allocations is NOT available in product | json,
    // so we use the offer data JSON (data-ps-sp-offer-data) which has allocation data per variant.
    var variantPrice = variantOfferData ? variantOfferData.variant_price : variant.price;
    var variantCompareAt = variantOfferData ? variantOfferData.variant_compare_at_price : variant.compare_at_price;

    var spCards = details.querySelectorAll('[data-ps-sp-card]');
    spCards.forEach(function (card) {
      var cardPlanId = card.getAttribute('data-ps-selling-plan-id') || '';
      var cardCurrentPrice = card.querySelector('[data-ps-sp-current-price]');
      var cardComparePrice = card.querySelector('[data-ps-sp-compare-price]');

      if (cardPlanId) {
        // Subscribe card — find allocation from offer data
        var alloc = null;
        if (variantOfferData && variantOfferData.allocations) {
          for (var j = 0; j < variantOfferData.allocations.length; j++) {
            if (String(variantOfferData.allocations[j].selling_plan_id) === cardPlanId) {
              alloc = variantOfferData.allocations[j];
              break;
            }
          }
        }

        if (alloc) {
          var displaySubPrice = (variantOfferData && variantOfferData.offer_subscription_price)
            ? variantOfferData.offer_subscription_price
            : alloc.per_delivery_price;
          if (cardCurrentPrice) cardCurrentPrice.textContent = formatMoney(displaySubPrice, format);
          if (cardComparePrice) {
            // Compare subscription price against regular variant price
            if (variantPrice > displaySubPrice) {
              cardComparePrice.textContent = formatMoney(variantPrice, format);
              cardComparePrice.hidden = false;
            } else {
              cardComparePrice.hidden = true;
            }
          }

          // Update description with selling plan name + actual allocation price (if not manually set)
          var descEl = card.querySelector('[data-ps-sp-description]');
          if (descEl && !descEl.hasAttribute('data-ps-sp-desc-manual')) {
            var planName = alloc.selling_plan_name || '';
            var planDeliveryPrice = alloc.per_delivery_price;
            if (planName) {
              // Strip baked-in price from plan name (e.g. "Deliver every 3 months, $99.00")
              var nameOnly = planName.split(/,\s*\$/)[0] || planName.split('$')[0] || planName;
              descEl.textContent = nameOnly.replace(/,\s*$/, '').trim();
            }
          }
        }

        // Update variant-level offer content on subscribe card
        if (variantOfferData) {
          // Title — update text node only, preserve badge span
          var titleEl = card.querySelector('[data-ps-sp-title]');
          if (titleEl && variantOfferData.offer_card_title) {
            var badgeSpan = titleEl.querySelector('[data-ps-sp-tag]');
            var titleText = variantOfferData.offer_card_title;
            titleEl.childNodes.forEach(function (node) {
              if (node.nodeType === 3 && node.textContent.trim()) {
                node.textContent = titleText;
              }
            });
            if (!titleEl.querySelector(':scope > :first-child') || titleEl.childNodes.length === 0) {
              titleEl.insertBefore(document.createTextNode(titleText), badgeSpan);
            }
          }

          // Badge
          var tagEl = card.querySelector('[data-ps-sp-tag]');
          if (tagEl) {
            if (variantOfferData.offer_badge_text) {
              tagEl.textContent = variantOfferData.offer_badge_text;
              tagEl.style.display = '';
            } else if (!tagEl.textContent.trim()) {
              tagEl.style.display = 'none';
            }
          }

          // Includes text (if not manually set)
          var incEl = card.querySelector('[data-ps-sp-includes]');
          if (incEl && !incEl.hasAttribute('data-ps-sp-inc-manual')) {
            if (variantOfferData.offer_includes_text) {
              incEl.textContent = variantOfferData.offer_includes_text;
              incEl.style.display = '';
            } else {
              incEl.textContent = '';
              incEl.style.display = 'none';
            }
          }

          // Per-unit text (if not manually set)
          var puEl = card.querySelector('[data-ps-sp-per-unit]');
          if (puEl && !puEl.hasAttribute('data-ps-sp-pu-manual')) {
            if (variantOfferData.offer_per_unit_text) {
              puEl.textContent = variantOfferData.offer_per_unit_text;
              puEl.style.display = '';
            } else {
              puEl.textContent = '';
              puEl.style.display = 'none';
            }
          }
        }
      } else if (!cardPlanId) {
        // One-time card — use variant price from offer data (or fallback to product JSON)
        if (cardCurrentPrice) cardCurrentPrice.textContent = formatMoney(variantPrice, format);
        if (cardComparePrice) {
          if (variantCompareAt && variantCompareAt > variantPrice) {
            cardComparePrice.textContent = formatMoney(variantCompareAt, format);
            cardComparePrice.hidden = false;
          } else {
            cardComparePrice.hidden = true;
          }
        }
      }
    });

    // Update bundle card prices — sum inner select variant prices in multi-item mode
    var bundleCards = details.querySelectorAll('[data-ps-bundle-card]');
    bundleCards.forEach(function (card) {
      var innerSelects = card.querySelectorAll('[data-ps-group-loft-select]');
      if (innerSelects && innerSelects.length > 0 && isMultiItemMode(card)) {
        // Multi-item mode — price is the sum of each select's variant
        updateInnerSelectPrices(card, product, format);
      } else {
        // Single mode — use the card's variant directly
        var bVariantId = card.getAttribute('data-ps-variant-id');
        var bVariant = findVariant(product, bVariantId);
        if (!bVariant) return;

        var bCurrentPrice = card.querySelector('[data-ps-bundle-price]');
        var bComparePrice = card.querySelector('[data-ps-bundle-compare-price]');

        if (bCurrentPrice) bCurrentPrice.textContent = formatMoney(bVariant.price, format);
        if (bComparePrice) {
          if (bVariant.compare_at_price && bVariant.compare_at_price > bVariant.price) {
            bComparePrice.textContent = formatMoney(bVariant.compare_at_price, format);
            bComparePrice.hidden = false;
          } else {
            bComparePrice.hidden = true;
          }
        }
      }
    });
  }

  /* ---- Listen for variant:update from theme's variant picker ---- */

  function initVariantListener() {
    document.addEventListener('variant:update', function (e) {
      // When the variant picker updates, recalculate prices in all product details containers
      var containers = document.querySelectorAll('.product-details');
      containers.forEach(function (container) {
        updatePrices(container);
      });
      syncAllTotalBars();
    });
  }

  /* ---- Shopify Design Mode ---- */

  function initDesignMode() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function () {
        // Re-run any initialization if needed
      });
    }
  }

  /* ---- Default selling plan sync on load ---- */

  function initDefaultSellingPlan() {
    var toggleContainers = document.querySelectorAll('[data-ps-selling-plan-toggle]');
    toggleContainers.forEach(function (container) {
      var activeCard = container.querySelector('[data-ps-sp-card][aria-checked="true"]');
      if (!activeCard) return;

      var sellingPlanId = activeCard.getAttribute('data-ps-selling-plan-id') || '';
      if (!sellingPlanId) return;

      var formComponent = findProductForm(container);
      if (!formComponent) return;

      var form = getForm(formComponent);
      if (!form) return;

      var input = getOrCreateSellingPlanInput(form);
      input.value = sellingPlanId;

      // Show subscription details if present
      var details = container.closest('.product-details');
      if (details) {
        var subDetails = details.querySelector('[data-ps-subscription-details]');
        if (subDetails) {
          subDetails.hidden = false;
        }
      }
    });
  }

  /* ---- Total bar direct update (fallback when updatePrices bails) ---- */

  /**
   * Directly recalculate the total bar from the current variant price + active contributors.
   * Used when the full updatePrices pipeline can't run (no product form, etc.).
   */
  function updateTotalBarFromContributors(container) {
    var totalBar = container.querySelector('[data-ps-total-bar]');
    if (!totalBar) return;

    var currentPriceEl = totalBar.querySelector('[data-ps-current-price]');
    var comparePriceEl = totalBar.querySelector('[data-ps-compare-price]');
    var saveEl = totalBar.querySelector('[data-ps-save-amount]');

    // Get base variant price from the form or from stored data
    var product = getProductJson(container);
    var form = container.querySelector('form[action*="/cart/add"]');
    var variantInput = form ? form.querySelector('input[name="id"]') : null;
    var basePrice = 0;
    var baseCompare = 0;

    if (product && variantInput && variantInput.value) {
      var variant = findVariant(product, variantInput.value);
      if (variant) {
        basePrice = variant.price;
        baseCompare = variant.compare_at_price || variant.price;
      }
    }

    if (basePrice === 0) return; // Can't determine base price

    var runningTotal = collectRunningTotal(container, basePrice, baseCompare);
    var totalSavings = runningTotal.compareTotal > runningTotal.total
      ? runningTotal.compareTotal - runningTotal.total : 0;

    var moneyFormat = container.closest('[data-money-format]');
    var format = moneyFormat ? moneyFormat.getAttribute('data-money-format') : null;

    if (currentPriceEl) currentPriceEl.textContent = formatMoney(runningTotal.total, format);
    if (comparePriceEl) {
      comparePriceEl.textContent = totalSavings > 0 ? formatMoney(runningTotal.compareTotal, format) : '';
      comparePriceEl.hidden = totalSavings <= 0;
    }
    if (saveEl) {
      saveEl.textContent = totalSavings > 0 ? 'Save ' + formatMoney(totalSavings, format) : '';
      saveEl.hidden = totalSavings <= 0;
    }
  }

  /**
   * Update ALL total bars on the page, including those outside .product-details.
   * Orphan total bars (not inside a .product-details) get their base price
   * from the first .product-details that has a product form.
   */
  function syncAllTotalBars() {
    // Find the primary product-details (the one with a product form)
    var primaryDetails = null;
    var containers = document.querySelectorAll('.product-details');
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].querySelector('form[action*="/cart/add"]')) {
        primaryDetails = containers[i];
        break;
      }
    }
    if (!primaryDetails) return;

    var product = getProductJson(primaryDetails);
    var form = primaryDetails.querySelector('form[action*="/cart/add"]');
    var variantInput = form ? form.querySelector('input[name="id"]') : null;
    if (!product || !variantInput || !variantInput.value) return;

    var variant = findVariant(product, variantInput.value);
    if (!variant) return;

    var basePrice = variant.price;
    var baseCompare = variant.compare_at_price || variant.price;

    // Check for selling plan allocation
    var offerDataEl = primaryDetails.querySelector('[data-ps-sp-offer-data]');
    var sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    var sellingPlanId = sellingPlanInput ? sellingPlanInput.value : '';
    if (sellingPlanId && offerDataEl) {
      try {
        var offerDataArr = JSON.parse(offerDataEl.textContent);
        for (var k = 0; k < offerDataArr.length; k++) {
          if (String(offerDataArr[k].variant_id) === variantInput.value && offerDataArr[k].allocations) {
            for (var a = 0; a < offerDataArr[k].allocations.length; a++) {
              if (String(offerDataArr[k].allocations[a].selling_plan_id) === sellingPlanId) {
                basePrice = offerDataArr[k].allocations[a].per_delivery_price;
                break;
              }
            }
            break;
          }
        }
      } catch (e) { /* ignore */ }
    }

    // Collect contributors from the primary details container
    var runningTotal = collectRunningTotal(primaryDetails, basePrice, baseCompare);
    var totalSavings = runningTotal.compareTotal > runningTotal.total
      ? runningTotal.compareTotal - runningTotal.total : 0;

    var moneyFormat = primaryDetails.closest('[data-money-format]');
    var format = moneyFormat ? moneyFormat.getAttribute('data-money-format') : null;

    // Update every total bar on the page
    document.querySelectorAll('[data-ps-total-bar]').forEach(function (totalBar) {
      var currentPriceEl = totalBar.querySelector('[data-ps-current-price]');
      var comparePriceEl = totalBar.querySelector('[data-ps-compare-price]');
      var saveEl = totalBar.querySelector('[data-ps-save-amount]');

      if (currentPriceEl) currentPriceEl.textContent = formatMoney(runningTotal.total, format);
      if (comparePriceEl) {
        comparePriceEl.textContent = totalSavings > 0 ? formatMoney(runningTotal.compareTotal, format) : '';
        comparePriceEl.hidden = totalSavings <= 0;
      }
      if (saveEl) {
        saveEl.textContent = totalSavings > 0 ? 'Save ' + formatMoney(totalSavings, format) : '';
        saveEl.hidden = totalSavings <= 0;
      }
    });

    // Update every ATC button price on the page (including standalone ones outside product-details)
    document.querySelectorAll('[data-ps-atc-price]').forEach(function (atcPriceEl) {
      atcPriceEl.textContent = applyDecimalDisplay(formatMoney(runningTotal.total, format), atcPriceEl);
    });
    document.querySelectorAll('[data-ps-atc-compare-price]').forEach(function (atcCompareEl) {
      if (totalSavings > 0) {
        atcCompareEl.textContent = applyDecimalDisplay(formatMoney(runningTotal.compareTotal, format), atcCompareEl);
        atcCompareEl.hidden = false;
      } else {
        atcCompareEl.hidden = true;
      }
    });
  }

  /* ---- Cart Add-ons ---- */

  /**
   * Collect checked add-on items from [data-ps-cart-addons] and [data-ps-modular-addons] within a container.
   * Returns array of { variantId, isFree }.
   */
  function getActiveAddons(container) {
    var addons = [];
    var seen = {};
    if (!container) return addons;

    // Collect from ALL addon containers in this product-details scope
    // but deduplicate by variant ID (synced checkboxes may appear in multiple blocks)
    container.querySelectorAll('[data-ps-cart-addon]').forEach(function (card) {
      var checkbox = card.querySelector('.ps-cart-addons__input');
      if (!checkbox || !checkbox.checked) return;

      var variantId = card.getAttribute('data-addon-variant-id');
      if (!variantId || seen[variantId]) return;
      seen[variantId] = true;

      addons.push({
        variantId: variantId,
        isFree: !!card.querySelector('.ps-cart-addons__price--free')
      });
    });

    // Also collect from modular addon blocks
    container.querySelectorAll('[data-ps-modular-addon]').forEach(function (card) {
      var checkbox = card.querySelector('.ps-modular-addon__input');
      if (!checkbox || !checkbox.checked) return;

      var variantId = card.getAttribute('data-addon-variant-id');
      if (!variantId || seen[variantId]) return;
      seen[variantId] = true;

      addons.push({
        variantId: variantId,
        isFree: !!card.querySelector('.ps-modular-addon__price--free')
      });
    });

    return addons;
  }

  /**
   * Handle form submit when cart-addons are present but no bundle cards.
   * Follows same pattern as handleBundleSubmit.
   */
  function handleAddonSubmit(e, form) {
    e.preventDefault();
    e.stopImmediatePropagation();

    var details = form.closest('.product-details') || form.closest('.shopify-section');

    // Validate variant selection — flash the picker if nothing is checked
    var section = details || form.closest('.shopify-section');
    var variantPicker = section ? section.querySelector('variant-picker') : null;
    if (variantPicker && variantPicker.validateSelection && !variantPicker.validateSelection()) return;

    var addons = getActiveAddons(details);

    var variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) return;

    var quantityInput = form.querySelector('input[name="quantity"]');
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    var mainItem = {
      id: parseInt(variantInput.value, 10),
      quantity: quantity
    };

    var sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput && sellingPlanInput.value) {
      mainItem.selling_plan = parseInt(sellingPlanInput.value, 10);
    }

    // Inject offer.cart_text metafield as a visible line item property
    var cartTextMapEl = (details.closest('.shopify-section') || document).querySelector('[data-cart-text-map]');
    if (cartTextMapEl) {
      try {
        var cartTextMap = JSON.parse(cartTextMapEl.textContent);
        var cartText = cartTextMap[variantInput.value] || cartTextMap['_product'] || '';
        if (cartText) {
          if (!mainItem.properties) mainItem.properties = {};
          mainItem.properties['Details'] = cartText;
        }
      } catch (e) { /* ignore */ }
    }

    var items = [mainItem];

    var productFormComponent = form.closest('product-form-component');
    var parentProductId = productFormComponent ? (productFormComponent.dataset.productId || '') : '';

    for (var a = 0; a < addons.length; a++) {
      var addonItem = {
        id: parseInt(addons[a].variantId, 10),
        quantity: 1,
        properties: { _addon: 'true', _addon_parent: parentProductId }
      };
      if (addons[a].isFree) {
        addonItem.properties._free_gift = 'true';
      }
      items.push(addonItem);
    }

    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    var payload = { items: items };
    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
    }

    var addToCartComponent = form.querySelector('add-to-cart-component');
    if (addToCartComponent && addToCartComponent.animateAddToCart) {
      addToCartComponent.animateAddToCart();
    }

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(function (r) { return r.json(); })
    .then(function (response) {
      if (response.status) {
        // Error
        if (addToCartComponent && addToCartComponent.resetButton) addToCartComponent.resetButton();
        return;
      }

      // Fetch full cart for item count
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (cr) { return cr.json(); })
        .then(function (cartData) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              source: 'cart-addons',
              data: { itemCount: cartData.item_count, sections: response.sections }
            }
          }));
        });
    })
    .catch(function (err) {
      console.warn('Cart add-ons submit error:', err);
      if (addToCartComponent && addToCartComponent.resetButton) addToCartComponent.resetButton();
    });
  }

  /**
   * Initialize cart add-ons submit handling for pages without bundle cards.
   * If bundle cards are present, handleBundleSubmit already collects addons.
   */
  function initCartAddonsSubmit() {
    var addonContainers = document.querySelectorAll('[data-ps-cart-addons]');
    addonContainers.forEach(function (container) {
      var details = container.closest('.product-details') || container.closest('.shopify-section');
      if (!details) return;

      // If bundle cards or modular addons are in this same container, they already handle the submit
      if (details.querySelector('[data-ps-bundle-cards]')) return;
      if (details.querySelector('[data-ps-modular-addons]')) return;

      var productFormComp = details.querySelector('product-form-component[on\\:submit]');
      if (productFormComp) {
        productFormComp.setAttribute('data-original-on-submit', productFormComp.getAttribute('on:submit'));
        productFormComp.removeAttribute('on:submit');
      }

      var form = details.querySelector('form[action*="/cart/add"]');
      if (form && !form.hasAttribute('data-addon-submit-bound')) {
        form.setAttribute('data-addon-submit-bound', 'true');
        form.addEventListener('submit', function (e) {
          handleAddonSubmit(e, form);
        });
      }
    });

    // Sync aria-checked + price contributor on checkbox change
    // When multiple addon blocks exist on the page, sync matching variant IDs across all of them
    document.addEventListener('change', function (e) {
      if (!e.target.matches('.ps-cart-addons__input')) return;
      var card = e.target.closest('[data-ps-cart-addon]');
      if (card) {
        var isChecked = e.target.checked;
        var variantId = card.getAttribute('data-addon-variant-id');

        // Sync ALL addon cards with the same variant ID across the entire page
        document.querySelectorAll('[data-ps-cart-addon][data-addon-variant-id="' + variantId + '"]').forEach(function (sibling) {
          sibling.setAttribute('aria-checked', isChecked ? 'true' : 'false');
          sibling.setAttribute('data-ps-contributor-active', isChecked ? 'true' : 'false');
          var siblingCheckbox = sibling.querySelector('.ps-cart-addons__input');
          if (siblingCheckbox && siblingCheckbox !== e.target) {
            siblingCheckbox.checked = isChecked;
          }
        });

        // Update the originating product-details (for selling plan cards, bundle cards, etc.)
        var details = card.closest('.product-details') || card.closest('.shopify-section');
        if (details) {
          updatePrices(details);
          updateTotalBarFromContributors(details);
        }

        // Sync ALL total bars on the page (including those outside product-details)
        syncAllTotalBars();
      }
    });
  }

  /* ---- Upsell / Add-on Price Contributor Listeners ---- */

  function initPriceContributorListeners() {
    // Upsell checkbox changes → toggle contributor active state and recalc prices
    document.addEventListener('change', function (e) {
      var checkbox = e.target.closest('.ps-bundle-card__upsell-checkbox');
      if (!checkbox) return;

      var upsell = checkbox.closest('[data-ps-price-contributor]');
      if (upsell) {
        upsell.setAttribute('data-ps-contributor-active', checkbox.checked ? 'true' : 'false');
      }

      var container = checkbox.closest('.product-details');
      if (container) updatePrices(container);
      syncAllTotalBars();
    });

    // Extensibility: any block can dispatch ps:prices:changed to trigger a recalc
    document.addEventListener('ps:prices:changed', function (e) {
      var details = e.target.closest('.product-details');
      if (details) updatePrices(details);
      syncAllTotalBars();
    });
  }

  /* ---- Pre-selected variant ---- */

  /**
   * If a variant has offer.preselected metafield set to true and no ?variant= URL param,
   * programmatically select that variant in the variant picker on page load.
   */
  function initPreselectedVariant() {
    // Skip if URL already has a variant param
    if (new URLSearchParams(window.location.search).has('variant')) return;

    document.querySelectorAll('script[data-preselected-variant-id]').forEach(function (script) {
      var variantId;
      try {
        variantId = JSON.parse(script.textContent);
      } catch (e) { return; }
      if (!variantId) return;

      var section = script.closest('.shopify-section');
      if (!section) return;

      // Find the variant option element and click it
      var option = section.querySelector(
        '[data-variant-id="' + variantId + '"]'
      );
      if (option) {
        // For radio/button inputs inside labels
        var input = option.querySelector('input[type="radio"]') || option;
        if (input && input !== option) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          option.click();
        }
        return;
      }

      // Fallback: check select dropdowns
      var selects = section.querySelectorAll('variant-picker select');
      selects.forEach(function (select) {
        var matchingOption = select.querySelector('option[data-variant-id="' + variantId + '"]');
        if (matchingOption) {
          select.value = matchingOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* ---- Init ---- */

  function init() {
    initSellingPlanToggle();
    initBundleCards();
    initBundleCardSubmit();
    initCartAddonsSubmit();
    initSubscriptionDetails();
    initVariantListener();
    initPriceContributorListeners();
    initDesignMode();
    initDefaultSellingPlan();

    // Keep prefstest nav cart count in sync with all cart:update events
    document.addEventListener('cart:update', function (e) {
      var prefstestCount = document.querySelector('[data-prefstest-cart-count]');
      if (!prefstestCount) return;
      var itemCount = e.detail && e.detail.data ? e.detail.data.itemCount : null;
      if (itemCount != null) {
        prefstestCount.textContent = String(itemCount);
      }
    });

    // Pre-select variant from offer.preselected metafield
    initPreselectedVariant();

    // Run initial price calculation so selling plan cards reflect the current variant
    // and cart-addon contributors are correctly excluded when unchecked
    var containers = document.querySelectorAll('.product-details');
    containers.forEach(function (container) {
      updatePrices(container);
    });
    // Also run for prefstest sections that may contain addons
    document.querySelectorAll('[data-ps-section]').forEach(function (section) {
      if (!section.querySelector('.product-details')) {
        updatePrices(section);
      }
    });
    // Direct total bar update for pages with cart-addons (fallback)
    document.querySelectorAll('[data-ps-cart-addons]').forEach(function (addon) {
      var container = addon.closest('.product-details') || addon.closest('.shopify-section');
      if (container) updateTotalBarFromContributors(container);
    });

    // Sync all total bars on the page (including orphans outside product-details)
    syncAllTotalBars();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
